/**
 * magicSyntaxPlugin — CodeMirror ViewPlugin that drives decoration updates.
 */

import type { EditorState } from "@codemirror/state";
import { foldEffect, unfoldEffect } from "@codemirror/language";
import { ViewPlugin, type ViewUpdate } from "@codemirror/view";
import {
    buildDecorations,
    collectMarkdownMathExcludedLines,
    type MagicBlockScan,
} from "./build-decorations";
import { magicScanField } from "./scan-state";
import {
    FOR_CLOSE_RE,
    FOR_HEADER_RE,
    INLINE_RENDER_RE,
    isInsideCodeSpan,
    REF_RE,
} from "./patterns";
import {
    collectMarkdownReferenceDefinitions,
    findMarkdownLinkSyntaxMatches,
    parseMarkdownReferenceDefinitionSyntax,
    type MarkdownReferenceDefinitionMap,
} from "./markdown-link/scan";
import {
    collectMarkdownMathBlockRanges,
    findMarkdownInlineMathSyntaxMatches,
} from "./markdown-math/scan";
import {
    collectMarkdownRichBlockRanges,
    findMarkdownRichInlineMatches,
} from "./markdown-rich/scan";
import {
    setEngineDefinitions,
    setExecutionDurations,
    setExecutionErrors,
    setExecutionStatuses,
    setForceL2Mode,
    setForExecutionDurations,
    setForExecutionStatuses,
    setInlineRenderedDefinitions,
    setMagicSyntaxFocused,
    setParseDiagnostics,
    setRefResolveContext,
    setWatchedUnitIds,
} from "./state-fields";

const INLINE_RENDER_RE_LOCAL = new RegExp(INLINE_RENDER_RE.source);

function isMagicSyntaxProfilingEnabled(): boolean {
    const globalObject = globalThis as typeof globalThis & {
        __MAGIC_MD_PROFILE_SYNTAX__?: boolean;
        localStorage?: Storage;
    };

    if (globalObject.__MAGIC_MD_PROFILE_SYNTAX__) return true;

    try {
        return (
            globalObject.localStorage?.getItem("magic-md:profile-syntax") === "1"
        );
    } catch {
        return false;
    }
}

function emitMagicSyntaxProfileSample(sample: {
    phase: "init" | "update";
    durationMs: number;
    docLines: number;
    visibleLineCount: number;
    reasons: string[];
}): void {
    if (!isMagicSyntaxProfilingEnabled()) return;

    const globalObject = globalThis as typeof globalThis & {
        dispatchEvent?: (event: Event) => boolean;
        console?: Console;
    };

    if (typeof CustomEvent !== "undefined" && globalObject.dispatchEvent) {
        globalObject.dispatchEvent(
            new CustomEvent("magic-md:syntax-profile", {
                detail: sample,
            }),
        );
    }

    globalObject.console?.debug?.("[magic-syntax] rebuild", sample);
}

function countVisibleLines(update: Pick<ViewUpdate, "view">): number {
    const visibleRanges = Array.isArray(update.view.visibleRanges)
        ? update.view.visibleRanges
        : [{ from: 0, to: update.view.state.doc.length }];

    let visibleLineCount = 0;
    for (const range of visibleRanges) {
        const startLine = update.view.state.doc.lineAt(range.from).number;
        const endLine = update.view.state.doc.lineAt(
            Math.max(range.from, range.to - 1),
        ).number;
        visibleLineCount += endLine - startLine + 1;
    }
    return visibleLineCount;
}

function lineHasInteractiveInlineSyntax(text: string): boolean {
    if (text.includes("::render[")) {
        const renderMatch = text.match(INLINE_RENDER_RE_LOCAL);
        if (renderMatch?.index != null) {
            const escapedByBackslash =
                renderMatch.index > 0 && text[renderMatch.index - 1] === "\\";
            const insideBackticks = isInsideCodeSpan(text, renderMatch.index);
            if (!escapedByBackslash && !insideBackticks) {
                return true;
            }
        }
    }

    if (!text.includes("$ref{")) return false;

    for (const match of text.matchAll(REF_RE)) {
        if (match.index == null) continue;
        if (isInsideCodeSpan(text, match.index)) continue;
        return true;
    }

    return false;
}

function selectionTouchesRange(
    state: EditorState,
    from: number,
    to: number,
): boolean {
    for (const range of state.selection.ranges) {
        if (range.empty) {
            if (range.head >= from && range.head <= to) return true;
            continue;
        }
        if (range.from < to && range.to > from) return true;
    }
    return false;
}

function getMarkdownLinkSelectionSignature(
    state: EditorState,
    lineNumber: number,
    text: string,
    references: MarkdownReferenceDefinitionMap,
): string | null {
    const matches = findMarkdownLinkSyntaxMatches(text, references);
    if (matches.length === 0) return null;

    const line = state.doc.line(lineNumber);
    const activeRanges = matches
        .filter((match) =>
            selectionTouchesRange(
                state,
                line.from + match.fromIndex,
                line.from + match.toIndex,
            ),
        )
        .map((match) => `${match.fromIndex}-${match.toIndex}`);

    return activeRanges.length > 0
        ? `markdown-link:${lineNumber}:${activeRanges.join(",")}`
        : `markdown-link:${lineNumber}:none`;
}

function getMarkdownMathSelectionSignature(
    state: EditorState,
    lineNumber: number,
    text: string,
): string | null {
    const matches = findMarkdownInlineMathSyntaxMatches(text);
    if (matches.length === 0) return null;

    const line = state.doc.line(lineNumber);
    const activeRanges = matches
        .filter((match) =>
            selectionTouchesRange(
                state,
                line.from + match.fromIndex,
                line.from + match.toIndex,
            ),
        )
        .map((match) => `${match.fromIndex}-${match.toIndex}`);

    return activeRanges.length > 0
        ? `markdown-math:${lineNumber}:${activeRanges.join(",")}`
        : `markdown-math:${lineNumber}:none`;
}

function getMarkdownRichInlineSelectionSignature(
    state: EditorState,
    lineNumber: number,
    text: string,
): string | null {
    const matches = findMarkdownRichInlineMatches(text);
    if (matches.length === 0) return null;

    const line = state.doc.line(lineNumber);
    const activeRanges = matches
        .filter((match) =>
            selectionTouchesRange(
                state,
                line.from + match.fromIndex,
                line.from + match.toIndex,
            ),
        )
        .map((match) => `${match.kind}:${match.fromIndex}-${match.toIndex}`);

    return activeRanges.length > 0
        ? `markdown-rich:${lineNumber}:${activeRanges.join(",")}`
        : `markdown-rich:${lineNumber}:none`;
}

export function getMagicSyntaxSelectionSignature(
    view: Pick<ViewUpdate["view"], "state" | "hasFocus">,
    scan: MagicBlockScan,
): string {
    if (!view.hasFocus) return "";

    const syntaxTargets = new Set<string>();
    const lines = new Set<number>();
    let markdownReferenceDefinitions: MarkdownReferenceDefinitionMap | null = null;
    let markdownMathBlockRanges: ReturnType<
        typeof collectMarkdownMathBlockRanges
    > | null = null;
    let markdownRichBlockRanges: ReturnType<
        typeof collectMarkdownRichBlockRanges
    > | null = null;

    for (const range of view.state.selection.ranges) {
        lines.add(view.state.doc.lineAt(range.head).number);
        if (!range.empty) {
            lines.add(view.state.doc.lineAt(range.anchor).number);
        }
    }

    for (const lineNumber of [...lines].sort((left, right) => left - right)) {
        const defineBlock = scan.defineBlockByLine[lineNumber];
        if (defineBlock) {
            syntaxTargets.add(`define:${defineBlock.defId}`);
            continue;
        }

        const metaBlock = scan.metaBlockByLine[lineNumber];
        if (metaBlock) {
            syntaxTargets.add(`meta:${metaBlock.startLine}`);
            continue;
        }

        const useBlock = scan.useBlockByLine[lineNumber];
        if (useBlock) {
            syntaxTargets.add(`use:${useBlock.startLine}`);
            continue;
        }

        const contentBlock = scan.contentBlockByLine[lineNumber];
        if (contentBlock) {
            syntaxTargets.add(`content:${contentBlock.startLine}`);
            continue;
        }

        const importBlock = scan.importBlockByLine[lineNumber];
        if (importBlock) {
            syntaxTargets.add(`import:${importBlock.lineNumber}`);
            continue;
        }

        const importLine = scan.importByLine[lineNumber];
        if (importLine) {
            syntaxTargets.add(`import:${importLine.lineNumber}`);
            continue;
        }

        const commentBlock = scan.htmlCommentBlockByLine[lineNumber];
        if (commentBlock) {
            syntaxTargets.add(`comment:${commentBlock.startLine}`);
            continue;
        }

        const text = view.state.doc.line(lineNumber).text;
        const trimmedStart = text.trimStart();
        const forMatch = trimmedStart.match(FOR_HEADER_RE);
        if (forMatch) {
            syntaxTargets.add(`for:${forMatch[1]}`);
            continue;
        }

        if (FOR_CLOSE_RE.test(trimmedStart)) {
            syntaxTargets.add(`endfor:${lineNumber}`);
            continue;
        }

        if (parseMarkdownReferenceDefinitionSyntax(text)) {
            syntaxTargets.add(`markdown-refdef:${lineNumber}`);
            continue;
        }

        markdownMathBlockRanges ??= collectMarkdownMathBlockRanges(
            view.state.doc,
            collectMarkdownMathExcludedLines(scan),
        );
        const markdownMathBlock = markdownMathBlockRanges.find(
            (range) =>
                lineNumber >= range.startLine && lineNumber <= range.endLine,
        );
        if (markdownMathBlock) {
            syntaxTargets.add(
                `markdown-math-block:${markdownMathBlock.startLine}-${markdownMathBlock.endLine}`,
            );
            continue;
        }

        markdownRichBlockRanges ??= collectMarkdownRichBlockRanges(
            view.state.doc,
            collectMarkdownMathExcludedLines(scan),
            new Set(
                markdownMathBlockRanges.flatMap((range) => {
                    const lines: number[] = [];
                    for (let ln = range.startLine; ln <= range.endLine; ln += 1) {
                        lines.push(ln);
                    }
                    return lines;
                }),
            ),
        );
        const markdownRichBlock = markdownRichBlockRanges.find(
            (range) =>
                lineNumber >= range.startLine && lineNumber <= range.endLine,
        );
        if (markdownRichBlock) {
            syntaxTargets.add(
                `markdown-rich-block:${markdownRichBlock.kind}:${markdownRichBlock.startLine}-${markdownRichBlock.endLine}`,
            );
            continue;
        }

        const markdownLinkSignature = getMarkdownLinkSelectionSignature(
            view.state,
            lineNumber,
            text,
            markdownReferenceDefinitions ??
                (markdownReferenceDefinitions =
                    collectMarkdownReferenceDefinitions(view.state.doc)),
        );
        const markdownMathSignature = getMarkdownMathSelectionSignature(
            view.state,
            lineNumber,
            text,
        );
        const markdownRichInlineSignature = getMarkdownRichInlineSelectionSignature(
            view.state,
            lineNumber,
            text,
        );
        if (markdownLinkSignature && !markdownLinkSignature.endsWith(":none")) {
            syntaxTargets.add(markdownLinkSignature);
            continue;
        }
        if (markdownMathSignature && !markdownMathSignature.endsWith(":none")) {
            syntaxTargets.add(markdownMathSignature);
            continue;
        }
        if (markdownRichInlineSignature && !markdownRichInlineSignature.endsWith(":none")) {
            syntaxTargets.add(markdownRichInlineSignature);
            continue;
        }
        if (markdownLinkSignature) {
            syntaxTargets.add(markdownLinkSignature);
            continue;
        }
        if (markdownMathSignature) {
            syntaxTargets.add(markdownMathSignature);
            continue;
        }
        if (markdownRichInlineSignature) {
            syntaxTargets.add(markdownRichInlineSignature);
            continue;
        }

        if (lineHasInteractiveInlineSyntax(text)) {
            syntaxTargets.add(`inline:${lineNumber}`);
        }
    }

    return [...syntaxTargets].sort().join("|");
}

function buildMagicSyntaxDecorationsWithProfiling(
    update: Pick<ViewUpdate, "view">,
    scan: MagicBlockScan,
    phase: "init" | "update",
    reasons: string[],
) {
    const start = performance.now();
    const decorations = buildDecorations(update.view, scan);
    const durationMs = performance.now() - start;

    emitMagicSyntaxProfileSample({
        phase,
        durationMs,
        docLines: update.view.state.doc.lines,
        visibleLineCount: countVisibleLines(update),
        reasons,
    });

    return decorations;
}

export function hasMagicSyntaxStateEffect(
    update: Pick<ViewUpdate, "transactions">,
): boolean {
    return update.transactions.some((tr) =>
        tr.effects.some(
            (e) =>
                e.is(setExecutionStatuses) ||
                e.is(setExecutionErrors) ||
                e.is(setExecutionDurations) ||
                e.is(setForExecutionStatuses) ||
                e.is(setForExecutionDurations) ||
                e.is(setInlineRenderedDefinitions) ||
                e.is(setWatchedUnitIds) ||
                e.is(setMagicSyntaxFocused) ||
                e.is(setParseDiagnostics) ||
                e.is(setRefResolveContext) ||
                e.is(setEngineDefinitions) ||
                e.is(foldEffect) ||
                e.is(unfoldEffect),
        ),
    );
}

export function shouldRebuildMagicSyntaxDecorations({
    docChanged,
    viewportChanged,
    syntaxStateChanged,
    activeBlockChanged,
    forceL2Changed,
    focusChanged,
    hasFocus,
}: {
    docChanged: boolean;
    viewportChanged: boolean;
    syntaxStateChanged: boolean;
    activeBlockChanged: boolean;
    forceL2Changed: boolean;
    focusChanged: boolean;
    hasFocus: boolean;
}): boolean {
    return (
        docChanged ||
        viewportChanged ||
        syntaxStateChanged ||
        activeBlockChanged ||
        forceL2Changed ||
        (focusChanged && !hasFocus)
    );
}

export const magicSyntaxPlugin = ViewPlugin.define(
    (view) => {
        // Read the cached scan from the shared StateField (computed once per doc change).
        let analysis = view.state.field(magicScanField);
        let scan = analysis.scan;
        let lastDraftContentCloseLine = analysis.draftContentCloseLine;
        let lastSelectionSignature = getMagicSyntaxSelectionSignature(view, scan);
        return {
            decorations: buildMagicSyntaxDecorationsWithProfiling(
                { view } as Pick<ViewUpdate, "view">,
                scan,
                "init",
                ["initial"],
            ),
            update(update: ViewUpdate) {
                const syntaxStateChanged = hasMagicSyntaxStateEffect(update);

                const forceL2Changed = update.transactions.some((tr) =>
                    tr.effects.some((e) => e.is(setForceL2Mode)),
                );

                // Only selection moves that change a syntax-relevant context
                // should trigger a rebuild.  Plain-text cursor motion should not.
                let activeBlockChanged = false;
                if (!update.docChanged && update.selectionSet && update.view.hasFocus) {
                    const nextSelectionSignature = getMagicSyntaxSelectionSignature(
                        update.view,
                        scan,
                    );
                    if (nextSelectionSignature !== lastSelectionSignature) {
                        lastSelectionSignature = nextSelectionSignature;
                        activeBlockChanged = true;
                    }
                }

                const nextAnalysis = update.state.field(magicScanField);
                const draftContentCloseLineChanged =
                    nextAnalysis.draftContentCloseLine !== lastDraftContentCloseLine;

                if (update.focusChanged && !update.view.hasFocus) {
                    lastSelectionSignature = "";
                }

                if (
                    shouldRebuildMagicSyntaxDecorations({
                        docChanged: update.docChanged,
                        viewportChanged: update.viewportChanged,
                        syntaxStateChanged,
                        activeBlockChanged: activeBlockChanged || draftContentCloseLineChanged,
                        forceL2Changed,
                        focusChanged: update.focusChanged,
                        hasFocus: update.view.hasFocus,
                    })
                ) {
                    if (update.docChanged || draftContentCloseLineChanged) {
                        // Read the updated scan from the StateField (already recomputed).
                        analysis = nextAnalysis;
                        scan = analysis.scan;
                        lastDraftContentCloseLine = analysis.draftContentCloseLine;
                        if (update.view.hasFocus) {
                            lastSelectionSignature = getMagicSyntaxSelectionSignature(
                                update.view,
                                scan,
                            );
                        }
                    }

                    const reasons: string[] = [];
                    if (update.docChanged) reasons.push("doc");
                    if (update.viewportChanged) reasons.push("viewport");
                    if (syntaxStateChanged) reasons.push("syntax-state");
                    if (activeBlockChanged) reasons.push("selection-context");
                    if (draftContentCloseLineChanged) reasons.push("draft-content-close");
                    if (forceL2Changed) reasons.push("force-l2");
                    if (update.focusChanged)
                        reasons.push(update.view.hasFocus ? "focus" : "blur");

                    this.decorations = buildMagicSyntaxDecorationsWithProfiling(
                        update,
                        scan,
                        "update",
                        reasons,
                    );
                }
            },
        };
    },
    {
        decorations: (v) => v.decorations,
    },
);
